import React, { PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { Z_LAYERS } from '@styles/zLayers.ts';
import SvgResizeCorner from '@icons/resize-corner.svg?react';
import { FLOAT_MIN_SIZE, readPanelPlacement, writePanelPlacement } from './panelDockStore.ts';
import { PanelRect, ResizeEdge, resizeFloatRect } from './panelGeometry.ts';
import { PanelDock } from './usePanelDock.ts';

interface ComponentProps {
  dock: PanelDock;
  className?: string;
}

// Invisible hit strips along every edge and corner, inside the window's bounds (it clips its overflow).
// Corners come last so they sit above the edges they overlap.
const RESIZE_HANDLES: { edge: ResizeEdge; className: string }[] = [
  { edge: 'n', className: 'left-3 right-3 top-0 h-1.5 cursor-ns-resize' },
  { edge: 's', className: 'bottom-0 left-3 right-3 h-1.5 cursor-ns-resize' },
  { edge: 'w', className: 'bottom-3 left-0 top-3 w-1.5 cursor-ew-resize' },
  { edge: 'e', className: 'bottom-3 right-0 top-3 w-1.5 cursor-ew-resize' },
  { edge: 'nw', className: 'left-0 top-0 h-3 w-3 cursor-nwse-resize' },
  { edge: 'ne', className: 'right-0 top-0 h-3 w-3 cursor-nesw-resize' },
  { edge: 'sw', className: 'bottom-0 left-0 h-3 w-3 cursor-nesw-resize' },
];

/**
 * A dockable panel's floating form: a non-modal window over the page, moved by its header (see
 * usePanelDrag) and resized from any edge or corner.
 *
 * The bottom-right corner carries a visible grip — the one place people look for a resize handle; the
 * other edges only change the cursor, as window edges do. Not the browser's `resize: both`: its handle
 * is a few pixels, cannot be styled in Firefox, and can only grow the window right and down — useless
 * for a window pushed against the right edge. Like the drag, a resize writes straight to the element
 * and persists on release.
 *
 * While a drag is heading for an edge, a tinted outline shows the dock it will snap to on release.
 * Minimized, the window stays mounted but hidden, like the docked panel does.
 */
const FloatingPanel: React.FC<PropsWithChildren<ComponentProps>> = ({ dock, className, children }) => {
  const { floatVisible, floatRect, floatRef, snapPreview, minimized, key } = dock;

  if (!floatVisible) return null;

  const onResizeStart = (edge: ResizeEdge) => (e: React.PointerEvent<HTMLElement>) => {
    const el = floatRef.current;
    if (e.button !== 0 || !el) return;
    e.preventDefault();
    e.stopPropagation();
    const box = el.getBoundingClientRect();
    const start: PanelRect = { x: box.left, y: box.top, w: box.width, h: box.height };
    let rect = start;
    document.body.classList.add('p-unselectable-text');

    const onMove = (ev: PointerEvent) => {
      rect = resizeFloatRect(start, edge, ev.clientX - e.clientX, ev.clientY - e.clientY);
      Object.assign(el.style, { left: `${rect.x}px`, top: `${rect.y}px`, width: `${rect.w}px`, height: `${rect.h}px` });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.classList.remove('p-unselectable-text');
      if (rect !== start) writePanelPlacement(key, { ...readPanelPlacement(key), ...rect });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  return createPortal(
    <>
      {snapPreview && (
        <div
          aria-hidden
          className="pointer-events-none fixed bottom-0 top-0 border-2 border-dashed border-content-brand bg-content-brand/10"
          style={{
            zIndex: Z_LAYERS.panel,
            width: 'min(550px, 100vw)',
            // The same placement rules as the docked panel in styles/base/sidebar.scss.
            ...(snapPreview === 'left'
              ? { left: 'max(0px, calc(var(--app-content-left, 0px) - var(--app-gutter, 0px)))' }
              : { right: 'max(0px, calc(var(--app-rest-width, 0px) - 550px))' }),
          }}
        />
      )}
      <div
        ref={floatRef}
        data-panel-float
        role="dialog"
        className={twMerge(
          'fixed flex flex-col overflow-hidden rounded-lg border border-solid border-content-quaternary shadow-[0_2px_8px_0_rgba(0,0,0,0.08),0_16px_48px_0_rgba(0,0,0,0.12)]',
          minimized && 'invisible pointer-events-none',
          className,
        )}
        style={{
          zIndex: Z_LAYERS.panel,
          left: floatRect.x,
          top: floatRect.y,
          width: floatRect.w,
          height: floatRect.h,
          minWidth: FLOAT_MIN_SIZE.w,
          minHeight: FLOAT_MIN_SIZE.h,
        }}
      >
        {children}
        {RESIZE_HANDLES.map(({ edge, className: handleClassName }) => (
          <div key={edge} aria-hidden onPointerDown={onResizeStart(edge)} className={`absolute z-10 ${handleClassName}`} />
        ))}
        {/* The visible corner grip, which is also the bottom-right handle. */}
        <div
          aria-hidden
          onPointerDown={onResizeStart('se')}
          className="absolute bottom-0 right-0 z-10 flex h-5 w-5 cursor-nwse-resize items-end justify-end p-0.5 text-content-tertiary transition-colors duration-200 hover:text-content-brand"
        >
          <SvgResizeCorner className="h-3.5 w-3.5" />
        </div>
      </div>
    </>,
    document.body,
  );
};

export default FloatingPanel;
